import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function RefundPolicy() {
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                +1 (929) 268-8770
              </span>
              <span className="flex items-center">
                <i className="ri-mail-line mr-2"></i>
                sam4104t@gmail.com
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-medicine-bottle-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-600">PillsForU</h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </Link>

            <div className="flex items-center space-x-6">
              <Link to="/cart" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Refund &amp; Return Policy</h1>
          <p className="text-xl text-blue-100">Your satisfaction is our priority</p>
        </div>
      </div>

      {/* Policy Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8 rounded-r-lg">
          <div className="flex items-start">
            <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full mr-4 flex-shrink-0">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">30-Day Money-Back Guarantee</h3>
              <p className="text-gray-700">We stand behind the quality of our products. If you're not completely satisfied, we'll make it right.</p>
            </div>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8 text-lg leading-relaxed">
            Last Updated: December 19, 2024
          </p>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-refund-line text-blue-600 mr-3"></i>
              Our Refund Commitment
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At PillsForU, we are committed to your satisfaction. We offer a comprehensive 30-day money-back guarantee on all products. If you are not completely satisfied with your purchase, we will provide a full refund or replacement.
            </p>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-checkbox-circle-line text-blue-600 mr-3"></i>
              Eligible Returns
            </h2>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">You can return products if:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">The product is unopened and in its original packaging</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">You received the wrong product or incorrect quantity</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">The product arrived damaged or defective</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">The product does not match the description on our website</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">The return is initiated within 30 days of delivery</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-close-circle-line text-red-600 mr-3"></i>
              Non-Returnable Items
            </h2>
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <p className="text-gray-700 mb-4">For health and safety reasons, we cannot accept returns of:</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Opened or used medications</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Products with broken seals or tampered packaging</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Prescription medications that have been dispensed</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Products returned after 30 days from delivery date</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-arrow-go-back-line text-blue-600 mr-3"></i>
              How to Initiate a Return
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-sm">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">1</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Contact Our Support Team</h3>
                    <p className="text-gray-700">Call us at +1 (929) 268-8770 or email sam4104t@gmail.com within 30 days of receiving your order. Provide your order number and reason for return.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-sm">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">2</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Receive Return Authorization</h3>
                    <p className="text-gray-700">Our team will review your request and provide a Return Authorization Number (RAN) along with return shipping instructions.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-sm">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">3</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Ship the Product Back</h3>
                    <p className="text-gray-700">Package the product securely in its original packaging. Include the RAN and your order number. Ship to the address provided by our team.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-sm">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full mr-4 flex-shrink-0 font-bold">4</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Receive Your Refund</h3>
                    <p className="text-gray-700">Once we receive and inspect your return, we will process your refund within 5-7 business days to your original payment method.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-exchange-line text-blue-600 mr-3"></i>
              Exchanges &amp; Replacements
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you received a damaged, defective, or incorrect product, we will replace it immediately at no additional cost. We will also cover return shipping costs for defective or incorrect items.
            </p>
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Replacement Process:</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span className="text-gray-700">Contact us immediately upon receiving a damaged or incorrect product</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span className="text-gray-700">Provide photos of the damaged product or packaging if applicable</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span className="text-gray-700">We will ship a replacement immediately, often before receiving the return</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-time-line text-blue-600 mr-3"></i>
              Refund Processing Time
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Processing Time</h3>
                <p className="text-gray-700">5-7 business days after we receive your returned item</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Credit Appearance</h3>
                <p className="text-gray-700">3-5 business days after processing, depending on your bank</p>
              </div>
            </div>
            <p className="text-gray-600 mt-4 text-sm">
              Note: Original shipping costs are non-refundable unless the return is due to our error (wrong item, defective product, etc.)
            </p>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-truck-line text-blue-600 mr-3"></i>
              Return Shipping
            </h2>
            <div className="space-y-4">
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  We Pay Return Shipping For:
                </h3>
                <ul className="space-y-2 ml-8">
                  <li className="text-gray-700">• Defective or damaged products</li>
                  <li className="text-gray-700">• Wrong items shipped</li>
                  <li className="text-gray-700">• Products not matching description</li>
                </ul>
              </div>
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-information-line text-orange-600 mr-2"></i>
                  Customer Pays Return Shipping For:
                </h3>
                <ul className="space-y-2 ml-8">
                  <li className="text-gray-700">• Change of mind returns</li>
                  <li className="text-gray-700">• Ordered wrong product</li>
                  <li className="text-gray-700">• No longer needed</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-customer-service-2-line text-blue-600 mr-3"></i>
              Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you have any questions about our refund policy or need assistance with a return, our customer support team is here to help 24/7.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border-2 border-gray-200 text-center hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-3">
                  <i className="ri-phone-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Phone</h3>
                <a href="tel:+19292688770" className="text-blue-600 hover:text-blue-700 cursor-pointer">+1 (929) 268-8770</a>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-gray-200 text-center hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
                  <i className="ri-mail-line text-2xl text-green-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Email</h3>
                <a href="mailto:sam4104t@gmail.com" className="text-blue-600 hover:text-blue-700 cursor-pointer">sam4104t@gmail.com</a>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-gray-200 text-center hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-3">
                  <i className="ri-whatsapp-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
                <a href="https://wa.me/19292688770" className="text-blue-600 hover:text-blue-700 cursor-pointer">Chat Now</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/all-products" className="hover:text-blue-400 transition-colors cursor-pointer">All Products</Link></li>
                <li><Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-blue-400 transition-colors cursor-pointer">Terms &amp; Conditions</Link></li>
                <li><Link to="/shipping-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Shipping Policy</Link></li>
                <li><Link to="/medical-disclaimer" className="hover:text-blue-400 transition-colors cursor-pointer">Medical Disclaimer</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p>Phone: +1 (929) 268-8770</p>
              <p>Email: sam4104t@gmail.com</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2024 PillsForU.com. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-blue-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
